import * as React from 'react';
import { Dropdown as BootstrapDropdown } from 'react-bootstrap';

import { isNullOrUndefined } from '@acceligentllc/shared/utils/extensions';

import { Button } from '@acceligentllc/storybook';

import styles from './styles.module.scss';
import TextSecondary from './TextSecondary';
import FlatListMenu from '../Dropdown/DropdownMenu/FlatListMenu';

type DropdownBase = { id: string | number; label: string; };

type WithoutControlledPrimaryOption = {
	controlPrimaryOption?: false;
	primaryOption?: never;
};

type WithControlledPrimaryOption<T> = {
	controlPrimaryOption: true;
	primaryOption: Nullable<T>;
};

type PrimaryOptionProps<T> = WithControlledPrimaryOption<T> | WithoutControlledPrimaryOption;

type WithoutControlledSecondaryOption = {
	controlSecondaryOption?: false;
	secondaryOption?: never;
	renderSecondaryFilter?: never;
};

type WithControlledSecondaryOption<T, U> = {
	controlSecondaryOption: true;
	secondaryOption: Nullable<U>;
	renderSecondaryFilter: (
		selectedPrimaryOption: Nullable<T>,
	) => React.ReactNode;
};

type SecondaryOptionProps<T, U> = WithControlledSecondaryOption<T, U> | WithoutControlledSecondaryOption;

type Props<T extends DropdownBase, U> = {
	defaultValue: Nullable<T>;
	placeholder: string | (() => React.ReactNode);
	options: T[] | null;
	/**
	 * Typed as both being Nullable since shouldDisableApplyButton can control button being disabled
	 * */
	onApplyButtonClick: (primaryOption: Nullable<T>, secondaryOption: Nullable<U>) => void;
	/** Key of the single option which will be used for primary filter
	 *	Defaults to "id".
	 * **/
	primaryFilterKey?: string;
	primaryFilterName?: string;
	onPrimaryOptionClick?: (primaryOption: T) => void;
	/**
	 * if ommited, defaults to T.label
	 * */
	renderPrimaryFilterItem?: (primaryOption: T) => React.ReactNode;
	secondaryFilterName?: string;
	filterable?: boolean;
	/** Function that filters options according to user's input text in the search box */
	filterBy?: (keyof T)[] | ((option: Nullable<T>, searchText: string) => boolean);
	onLazyLoad?: (isLazyLoaded: boolean) => Promise<void>;
	filterOptions?: (option: T, index: number) => boolean;
	shouldDisableApplyButton?: (primaryOption: Nullable<T>, secondaryOption: Nullable<U>) => boolean;
	renderSelected?: (selectedPrimary?: Nullable<T>, selectedSecondary?: Nullable<U>) => React.ReactNode;
	onClear?: () => void;
	withCaret?: boolean;
	toggleClassName?: string;
	initiallyOpened?: boolean;
} & PrimaryOptionProps<T> & SecondaryOptionProps<T, U>;

function DropdownWithSecondaryPicker<T extends DropdownBase, U = string>(props: React.PropsWithChildren<Props<T, U>>) {
	const {
		filterable,
		defaultValue,
		onLazyLoad,
		filterOptions,
		filterBy,
		options,
		primaryFilterKey,
		renderSecondaryFilter,
		primaryFilterName,
		secondaryFilterName,
		controlSecondaryOption,
		onPrimaryOptionClick,
		shouldDisableApplyButton,
		controlPrimaryOption,
		primaryOption: overridenPrimary,
		secondaryOption: overriddenSecondary,
		onApplyButtonClick,
		placeholder: placeholderProp,
		renderSelected: renderSelectedProp,
		onClear: onClearProp,
		withCaret = true,
		toggleClassName: toggleClassNameProp,
		renderPrimaryFilterItem,
		initiallyOpened,
	} = props;

	const [isLazyLoading, setIsLazyLoading] = React.useState(false);
	const lazyLoad = React.useCallback(async (lazyLoadedVal: boolean) => {
		if (!onLazyLoad) {
			return;
		}
		setIsLazyLoading(true);
		await onLazyLoad(lazyLoadedVal);
		setIsLazyLoading(false);
	}, [onLazyLoad]);

	const containerRef = React.useRef<HTMLDivElement>(null);
	const handleFocus = React.useCallback(() => {
		if (onLazyLoad) {
			const lazyLoadedVal = !!options && options.length > 0;
			lazyLoad(lazyLoadedVal);
		}
	}, [lazyLoad, onLazyLoad, options]);

	const [isOpen, setIsOpen] = React.useState(() => {
		if (initiallyOpened) {
			handleFocus();
			return true;
		}
		return false;
	});

	const [searchText, setSearchText] = React.useState('');
	const [selectedPrimary, setSelectedPrimary] = React.useState<Nullable<T>>(props.defaultValue ?? null);
	const [selectedSecondary, setSelectedSecondary] = React.useState<Nullable<U>>(null);
	const [filteredOptions, setFilteredOptions] = React.useState<Nullable<T[]>>(options);

	const currentPrimary = React.useMemo(() => {
		if (controlPrimaryOption) {
			return overridenPrimary;
		}
		return selectedPrimary;
	}, [controlPrimaryOption, overridenPrimary, selectedPrimary]);

	const currentSecondary = React.useMemo(() => {
		if (controlSecondaryOption) {
			return overriddenSecondary;
		}
		return selectedSecondary;
	}, [controlSecondaryOption, overriddenSecondary, selectedSecondary]);

	const itemRefs = React.useRef<HTMLSpanElement[]>([]);

	const handleToggle = React.useCallback(() => {
		const newValue = !isOpen;
		setIsOpen(newValue);
		if (newValue) {
			const selectedIndex = filteredOptions?.findIndex((_option) => _option.id === overridenPrimary?.id);
			if (selectedIndex !== undefined && selectedIndex > -1) {
				itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'center' });
			}
		}
	}, [filteredOptions, isOpen, overridenPrimary?.id]);

	const onClear = React.useCallback(() => {
		onClearProp?.();
		setSelectedPrimary(null);
		setSelectedSecondary(null);
	}, [onClearProp]);

	const placeholder = React.useMemo(() => {
		if (typeof placeholderProp === 'function') {
			return placeholderProp();
		}
		return <span className={styles.selected__placeholder}>{placeholderProp}</span>;
	}, [placeholderProp]);

	const renderSelected = React.useCallback(() => {
		const selected = renderSelectedProp
			? renderSelectedProp(currentPrimary, currentSecondary)
			: currentPrimary?.label;
		const clearable = !!onClearProp;
		const hasIcons = clearable || withCaret;

		return (
			<div
				className={styles.selected}
				onClick={handleToggle}
			>
				{isNullOrUndefined(currentPrimary) && isNullOrUndefined(currentSecondary)
					? placeholder
					: <span className={styles.label}>{selected}</span>
				}
				{hasIcons && (
					<div className={styles.selected__icons}>
						{clearable ? <span className={'icon-close'} onClick={onClear} /> : null}
						{withCaret && <span className="icon-down" />}
					</div>)
				}
			</div>
		);
	}, [currentPrimary, currentSecondary, handleToggle, onClear, onClearProp, placeholder, renderSelectedProp, withCaret]);

	const handleChange = React.useCallback((item: string) => {
		const originalItem = options?.find((_option) => {
			if (_option[primaryFilterKey ?? 'id'] === null && item === null) {
				return true;
			}
			return _option[primaryFilterKey ?? 'id']?.toString() === item;
		});
		setSelectedPrimary(originalItem ?? null);
	}, [options, primaryFilterKey]);

	const renderLoading = React.useCallback(() => {
		return (
			<BootstrapDropdown.Item
				disabled={true}
			>
				Loading...
			</BootstrapDropdown.Item>
		);
	}, []);

	const setRefForIndex = React.useCallback((index: number) => (el: HTMLSpanElement) => {
		if (el) {
			itemRefs.current[index] = el;
		}
	}, []);

	const onMenuItemClick = React.useCallback((option: T) => () => {
		onPrimaryOptionClick?.(option);
	}, [onPrimaryOptionClick]);

	const filterOption = React.useCallback((newSearchText: string) => (option: Nullable<T>): boolean => {
		if (!newSearchText) {
			return true;
		}
		const lowerSearch = newSearchText.toLowerCase();
		if (Array.isArray(filterBy)) {
			const searchWords = lowerSearch.replace(/\s\s+/g, ' ').split(' ');
			const searchableValues: string[] = [];
			for (const field of filterBy) {
				const value = option?.[field];
				if (value && typeof value === 'string') {
					searchableValues.push(value.toLowerCase());
				}
			}
			for (const word of searchWords) {
				let matched = false;
				for (const val of searchableValues) {
					if (val.includes(word)) {
						matched = true;
						break;
					}
				}
				if (!matched) return false;
			}
			return true;
		} else if (typeof filterBy === 'function') {
			return filterBy(option, lowerSearch);
		}

		return !!option?.label.toLowerCase().includes(newSearchText.toLowerCase());
	}, [filterBy]);

	const renderMenuItem = React.useCallback((item: T, index: number, _searchText: string, onChangeHandler) => {
		const selectedPrimaryClassName = (item.id === currentPrimary?.id) ? styles['primary-options__item--selected'] : '';
		const itemClassName = `${styles['primary-options__item']} ${selectedPrimaryClassName}`;
		const eventKey = item?.[primaryFilterKey ?? 'id'] as string;

		const isFiltered = !filterable || filterOption(_searchText)(item);
		if (!isFiltered) {
			return null;
		}

		return (
			<BootstrapDropdown.Item
				className={itemClassName}
				eventKey={eventKey !== undefined ? eventKey : ''}
				key={`menuItem#${index + 1}`}
				onClick={onMenuItemClick(item)}
				onSelect={onChangeHandler}
			>
				<span
					className={styles['primary-options__item__label']}
					ref={setRefForIndex(index)}
				>
					{renderPrimaryFilterItem ? renderPrimaryFilterItem(item) : item.label}
				</span>
				<span className="icon-right" />
			</BootstrapDropdown.Item>
		);
	}, [currentPrimary?.id, primaryFilterKey, filterable, filterOption, onMenuItemClick, setRefForIndex, renderPrimaryFilterItem]);

	const renderNoResults = React.useCallback(() => {
		return (
			<BootstrapDropdown.Item
				className={styles['primary-options__no-results']}
				disabled={true}
			>
				No results found
			</BootstrapDropdown.Item>
		);
	}, []);

	const onSearchInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(e.target.value);
		const newFilteredOptions = options?.filter(filterOption(e.target.value)) ?? [];
		setFilteredOptions(newFilteredOptions);
	}, [filterOption, options]);

	const renderDefaultSecondary = React.useCallback(() => {
		if (!currentPrimary) {
			return null;
		}

		return (
			<TextSecondary
				secondaryOption={selectedSecondary as Nullable<string>}
				setSecondaryOption={setSelectedSecondary as (value: Nullable<string>) => void}
			/>
		);
	}, [currentPrimary, selectedSecondary]);

	const clearSearchInput = React.useCallback(() => {
		setSearchText('');
		setFilteredOptions(options);
	}, [options]);

	const isApplyButtonDisabled = React.useMemo(() => {
		if (shouldDisableApplyButton) {
			return shouldDisableApplyButton(currentPrimary, overriddenSecondary ?? selectedSecondary);
		}
		return !currentPrimary || !selectedSecondary;
	}, [currentPrimary, overriddenSecondary, selectedSecondary, shouldDisableApplyButton]);

	const handleBlur: React.FocusEventHandler<HTMLDivElement> = React.useCallback((e) => {
		if (
			containerRef.current &&
			e.relatedTarget &&
			containerRef.current.contains(e.relatedTarget as Node)
		) {
			// focus inside dropdown
			return;
		}
		setIsOpen(false);
	}, []);

	const onApplyClick = React.useCallback(() => {
		setIsOpen(false);
		onApplyButtonClick(currentPrimary, overriddenSecondary ?? selectedSecondary);
	}, [onApplyButtonClick, overriddenSecondary, currentPrimary, selectedSecondary]);

	const toggleClassName = React.useMemo(() => {
		if (toggleClassNameProp) {
			return `${styles.toggle} ${toggleClassNameProp}`;
		}
		return styles.toggle;
	}, [toggleClassNameProp]);

	React.useEffect(() => {
		if (initiallyOpened) {
			containerRef.current?.focus();
		}
	}, [initiallyOpened]);

	return (
		<div onBlur={handleBlur} ref={containerRef} tabIndex={-1}>
			<BootstrapDropdown
				onFocus={handleFocus}
				show={isOpen}
			>
				<BootstrapDropdown.Toggle className={toggleClassName} onClick={handleToggle}>
					{renderSelected()}
				</BootstrapDropdown.Toggle>
				<BootstrapDropdown.Menu className={styles.container}>
					<div className={styles['primary-options']}>
						<div className={styles['primary-options__header']}>
							{primaryFilterName
								? (
									<div className={styles['primary-options__name']}>
										{primaryFilterName}
									</div>
								)
								: null
							}
							{filterable && (
								<a className={styles['search-input-container']}>
									<input
										className={styles['search-input']}
										onChange={onSearchInputChange}
										placeholder="Search"
										type="text"
										value={searchText}
									/>
									{searchText ? (
										<span className="icon-close" onClick={clearSearchInput} role="button" />
									) : (
										< span className="icon-search" />
									)}
								</a>
							)}
						</div>
						<FlatListMenu
							calculatedPropName="id"
							defaultValue={defaultValue}
							filterOptions={filterOptions}
							hasBlankOption={false}
							isLazyLoading={isLazyLoading}
							onChange={handleChange}
							options={options}
							renderLoading={renderLoading}
							renderMenuItem={renderMenuItem}
							renderNoResults={renderNoResults}
							searchText={searchText}
							selected={currentPrimary}
						/>
					</div>
					<div className={styles['secondary-options']}>
						{secondaryFilterName
							? (
								<div className={styles['secondary-options__name']}>
									{secondaryFilterName}
								</div>
							)
							: null
						}
						{!!controlSecondaryOption
							? renderSecondaryFilter(currentPrimary)
							: renderDefaultSecondary()
						}
					</div>
					<div className={styles['apply-button']}>
						<Button
							disabled={isApplyButtonDisabled}
							label="Apply Filters"
							onClick={onApplyClick}
							style="primary"
							type="submit"
						/>
					</div>
				</BootstrapDropdown.Menu>
			</BootstrapDropdown>
		</div>
	);
}

export default React.memo(DropdownWithSecondaryPicker) as typeof DropdownWithSecondaryPicker;

